import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShoppingBag, Check } from 'lucide-react';
import { openLink } from '@tma.js/sdk-react';

import {
  useGetIapUserQuery,
  useGetPurchasesQuery,
  useUpdateIapUserMutation,
} from '@/services/customizationApi';
import { cdnImage } from '@/lib/cdnImage';
import { cn } from '@/lib/cn';
import { env } from '@/env';
import { useHaptics } from '@/hooks/useHaptics';

interface BannerPickerProps {
  /** QLASH user id — resolves the IAP user record + their purchased banners. */
  userId: number | string | undefined | null;
}

/**
 * The owned-banner grid + "Get more banners" CTA, lifted out of the old
 * CustomizeSheet so it can live inline on the Appearance settings page. Owns
 * its own data + selection state; renders no overlay chrome.
 */
export function BannerPicker({ userId }: BannerPickerProps) {
  const { t } = useTranslation('general');
  const haptics = useHaptics();
  const { data: iapUser, isLoading: iapLoading } = useGetIapUserQuery(userId ?? '', {
    skip: userId == null,
  });
  const iapUserId = iapUser?._id;
  const { data: purchases, isLoading: purchasesLoading } = useGetPurchasesQuery(
    { user: iapUserId ?? '' },
    { skip: !iapUserId },
  );
  const [updateIapUser, mutation] = useUpdateIapUserMutation();

  const banners = useMemo(() => {
    const seen = new Set<string>();
    const out: { id: string; name: string; mediaUri: string; type: string }[] = [];
    (purchases ?? []).forEach((p) => {
      const b = p.banner;
      if (!b?._id || seen.has(b._id)) return;
      seen.add(b._id);
      out.push({
        id: b._id,
        name: b.name ?? '—',
        mediaUri: b.media_uri,
        type: b.type ?? 'common',
      });
    });
    return out;
  }, [purchases]);

  const activeBannerId = iapUser?.banner?._id ?? null;
  const [pendingId, setPendingId] = useState<string | null>(null);

  const handlePick = (id: string): void => {
    if (!iapUserId || mutation.isLoading) return;
    haptics.selection();
    setPendingId(id);
    updateIapUser({ id: iapUserId, body: { banner: id } })
      .unwrap()
      .catch((err) => {
        console.warn('[banner-picker] update failed:', err);
      })
      .finally(() => setPendingId(null));
  };

  const handleGetMore = (): void => {
    haptics.impact('light');
    openLink.ifAvailable(env.shopUrl);
  };

  const loading = iapLoading || purchasesLoading;

  return (
    <div className="flex flex-col gap-3">
      {loading && (
        <p className="py-6 text-center text-sm text-fg-muted">
          {t('banner.loading', { defaultValue: 'Loading your banners…' })}
        </p>
      )}

      {!loading && banners.length === 0 && (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          {t('banner.empty', {
            defaultValue:
              "You don't own any banners yet. Unlock some in the QLASH app shop.",
          })}
        </div>
      )}

      {!loading && banners.length > 0 && (
        <ul className="grid grid-cols-2 gap-3">
          {banners.map((b) => {
            const isActive = b.id === activeBannerId;
            const isPending = b.id === pendingId;
            return (
              <li key={b.id}>
                <button
                  type="button"
                  onClick={() => handlePick(b.id)}
                  disabled={mutation.isLoading}
                  className={cn(
                    'group relative flex w-full flex-col gap-2 overflow-hidden rounded-xl p-1 text-left transition-opacity',
                    isActive ? 'ring-2 ring-brand ring-offset-2 ring-offset-bg' : '',
                    mutation.isLoading && !isPending ? 'opacity-50' : 'active:opacity-80',
                  )}
                >
                  <div className="relative aspect-[16/9] overflow-hidden rounded-lg bg-bg-tertiary">
                    {b.mediaUri && (
                      <img
                        src={cdnImage(b.mediaUri)}
                        alt={b.name}
                        className="h-full w-full object-cover"
                        loading="lazy"
                      />
                    )}
                    {isActive && (
                      <span className="absolute right-1 top-1 flex h-6 w-6 items-center justify-center rounded-full bg-brand text-brand-fg">
                        <Check aria-hidden className="h-4 w-4" />
                      </span>
                    )}
                    {isPending && (
                      <span className="absolute inset-0 flex items-center justify-center bg-bg/60 text-xs font-semibold text-fg">
                        {t('banner.applying', { defaultValue: 'Applying…' })}
                      </span>
                    )}
                  </div>
                  <p className="px-1 text-xs font-semibold text-fg">{b.name}</p>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <button
        type="button"
        onClick={handleGetMore}
        className="flex w-full items-center justify-center gap-2 rounded-full bg-brand py-3 text-sm font-bold uppercase text-brand-fg transition-opacity active:opacity-80"
      >
        <ShoppingBag aria-hidden className="h-4 w-4" />
        {t('banner.get_more', { defaultValue: 'Get more banners' })}
      </button>
    </div>
  );
}
